interface ArticleYoutubeProps {
	/** Any YouTube URL (watch, youtu.be, shorts, embed). */
	src: string;
	/** Visible caption under the player. */
	caption?: string;
	/** Falls back to the caption; used as the iframe title. */
	alt?: string;
}

const YOUTUBE_ID_RES = [
	/youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)([\w-]{11})/i,
	/youtu\.be\/([\w-]{11})/i,
];

/** Extract the 11-char YouTube video id from a URL, or null when it isn't a YouTube link. */
export const youtubeIdFromUrl = (url: string): string | null => {
	for (const re of YOUTUBE_ID_RES) {
		const match = re.exec(url);
		if (match) return match[1];
	}
	return null;
};

/**
 * ArticleYoutube — figure for YouTube links in the article markdown renderer
 * (docs + blog). Mirrors ArticleVideo's framed figure, but embeds the
 * privacy-enhanced youtube-nocookie player instead of a native <video>.
 */
export const ArticleYoutube = ({ src, caption, alt }: ArticleYoutubeProps): React.JSX.Element => {
	const captionText = caption ?? alt;
	const videoId = youtubeIdFromUrl(src);
	if (!videoId) return <></>;
	return (
		<figure className="my-6">
			<div className="overflow-hidden rounded-lg bg-neutral/[0.03]">
				<iframe
					src={`https://www.youtube-nocookie.com/embed/${videoId}`}
					title={alt || captionText || 'YouTube video'}
					loading="lazy"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowFullScreen
					className="block aspect-video w-full border-0"
				/>
			</div>
			{captionText && (
				<figcaption className="mt-2.5 text-center text-[13px] leading-[1.5] text-neutral/40">
					{captionText}
				</figcaption>
			)}
		</figure>
	);
};
